// pages/account/mes-commandes/page.tsx
import { Package, ChevronRight } from "lucide-react";
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PageContainer } from "../../../components/ui/PageContainer";
import { Order } from "@cabukaka/types";
import { useOrders } from "@cabukaka/hooks";

export default function OrdersPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<string>("all");
  const { orders, loading } = useOrders();

  const statusLabels: Record<Order["status"], { label: string; color: string }> = {
    pending: { label: "En attente", color: "text-yellow-600 bg-yellow-100" },
    confirmed: { label: "Confirmée", color: "text-blue-600 bg-blue-100" },
    preparing: { label: "En préparation", color: "text-purple-600 bg-purple-100" },
    shipped: { label: "Expédiée", color: "text-indigo-600 bg-indigo-100" },
    delivered: { label: "Livrée", color: "text-green-600 bg-green-100" },
    cancelled: { label: "Annulée", color: "text-red-600 bg-red-100" }
  };

  const filteredOrders = useMemo(() => {
    if (filter === "all") return orders;
    return orders.filter(o => o.status === filter);
  }, [orders, filter]);

  const getStatusColor = (status: Order["status"]) => {
    return statusLabels[status]?.color || "text-gray-600 bg-gray-100";
  };

  const getStatusLabel = (status: Order["status"]) => {
    return statusLabels[status]?.label || status;
  };

  if (loading) {
    return (
      <PageContainer withContainer className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer withContainer className="space-y-6">
      <h1 className="text-xl md:text-2xl font-bold text-gray-900">Mes commandes</h1>

      {/* Filtres */}
      <div className="flex flex-wrap gap-2">
        {["all", "pending", "confirmed", "preparing", "shipped", "delivered", "cancelled"].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-sm md:text-base transition-colors ${
              filter === status
                ? "bg-primary text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {status === "all" ? "Toutes" : getStatusLabel(status as Order["status"])}
          </button>
        ))}
      </div>

      {/* Liste des commandes */}
      {filteredOrders.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Aucune commande trouvée</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((order) => (
            <div
              key={order.id}
              className="bg-white border border-gray-100 rounded-xl p-4 md:p-5 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/compte/mes-commandes/${order.id}`)}
            >
              <div className="flex flex-col md:flex-row gap-4">
                {/* Image du premier produit */}
                <div className="relative w-20 h-20 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden">
                  {order.items?.[0]?.product?.mainImage ? (
                    <img 
                      src={order.items[0].product.mainImage} 
                      alt="" 
                      className="w-full h-full object-cover" 
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Infos */}
                <div className="flex-1">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-2">
                    <div>
                      <p className="font-semibold text-gray-900">{order.reference}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(order.createdAt).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full self-start ${getStatusColor(order.status)}`}>
                      {getStatusLabel(order.status)}
                    </span>
                  </div>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                    <p className="text-sm text-gray-600">{order.items?.length || 0} article(s)</p>
                    <div className="flex items-center gap-4">
                      <p className="font-bold text-primary">{order.total.toLocaleString('fr-FR')} FCFA</p>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </PageContainer>
  );
}